import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";

const NOTIFY_KEY = "settings.emailNotifications";

export default function Settings() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user, refreshUser } = useAuth();

  const [name, setName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(
    () => localStorage.getItem(NOTIFY_KEY) !== "false"
  );

  useEffect(() => {
    setName(user?.name || "");
  }, [user?.name]);

  const onSaveProfile = async () => {
    setErr(null);
    setMsg(null);
    setSavingProfile(true);
    try {
      await api.patch("/api/users/me", { name: name.trim() });
      await refreshUser();
      setMsg("Profile updated.");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const onSavePassword = async () => {
    setErr(null);
    setMsg(null);
    if (!newPassword || newPassword.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setSavingPassword(true);
    try {
      await api.patch("/api/users/me", {
        currentPassword,
        newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMsg("Password updated.");
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Could not update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const onNotifyToggle = (e) => {
    const on = e.target.checked;
    setEmailNotifications(on);
    localStorage.setItem(NOTIFY_KEY, on ? "true" : "false");
  };

  return (
    <Box m="20px">
      <Header title="SETTINGS" subtitle="Account, security, and preferences" />

      {msg ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg}
        </Alert>
      ) : null}
      {err ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      ) : null}

      <Paper sx={{ p: 3, mb: 2, maxWidth: 560, backgroundColor: colors.primary[400] }}>
        <Typography variant="h6" gutterBottom>
          Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Display name and email (email is managed by an administrator).
        </Typography>
        <TextField
          label="Display name"
          fullWidth
          margin="dense"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField label="Email" fullWidth margin="dense" value={user?.email || ""} disabled />
        <Button
          variant="contained"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onSaveProfile}
          disabled={savingProfile || (name || "").trim() === (user?.name || "").trim()}
        >
          Save profile
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mb: 2, maxWidth: 560, backgroundColor: colors.primary[400] }}>
        <Typography variant="h6" gutterBottom>
          Security
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Change your sign-in password.
        </Typography>
        <TextField
          label="Current password"
          type="password"
          fullWidth
          margin="dense"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <TextField
          label="New password"
          type="password"
          fullWidth
          margin="dense"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          label="Confirm new password"
          type="password"
          fullWidth
          margin="dense"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <Button
          variant="contained"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onSavePassword}
          disabled={savingPassword || !currentPassword || !newPassword}
        >
          Update password
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mb: 2, maxWidth: 560, backgroundColor: colors.primary[400] }}>
        <Typography variant="h6" gutterBottom>
          Preferences
        </Typography>
        <FormControlLabel
          control={<Switch checked={emailNotifications} onChange={onNotifyToggle} color="secondary" />}
          label="Email notifications (product updates and alerts)"
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Theme (light / dark) is toggled from the sun or moon icon in the top bar.
        </Typography>
      </Paper>
    </Box>
  );
}
